import { useCallback, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import { v4 as uuid } from "uuid";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { isAccrualReportPackageType } from "@common/types/reports/report-package";
import { AccountingReportType } from "@common/types/reports/report-type";
import {
    buildReportPackageRowTitle,
    getDefaultReportPackageRowBasis,
    getReportPackageRowOptionDefaults,
    ReportPackageRow,
} from "./reportPackageRows.lib";

type ReportPackageRowPatch = Partial<
    Omit<ReportPackageRow, "id" | "title" | "isTitleEdited">
>;

function getUpdatedRow({
    row,
    patch,
    rows,
}: {
    row: ReportPackageRow;
    patch: ReportPackageRowPatch;
    rows: ReportPackageRow[];
}): ReportPackageRow {
    if (
        (patch.reportType === undefined ||
            patch.reportType === row.reportType) &&
        (patch.layout === undefined || patch.layout === row.layout)
    ) {
        return { ...row, ...patch };
    }
    return {
        ...row,
        ...patch,
        filters: undefined,
        basis: isAccrualReportPackageType(patch.reportType ?? row.reportType)
            ? AccountingLedgerBasis.accruals
            : row.basis,
        title: row.isTitleEdited
            ? row.title
            : buildReportPackageRowTitle({
                  reportType: patch.reportType ?? row.reportType,
                  layout: patch.layout,
                  takenTitles: rows
                      .filter((otherRow) => otherRow.id !== row.id)
                      .map((otherRow) => otherRow.title),
              }),
    };
}

export function useReportPackageRows() {
    const [rows, setRows] = useState<ReportPackageRow[]>([]);

    const addRow = useCallback((entityId?: number) => {
        setRows((prev) => {
            const reportType = AccountingReportType.PROFIT_LOSS;
            return [
                ...prev,
                {
                    id: uuid(),
                    entityId,
                    reportType,
                    basis: getDefaultReportPackageRowBasis(reportType),
                    title: buildReportPackageRowTitle({
                        reportType,
                        takenTitles: prev.map((row) => row.title),
                    }),
                    isTitleEdited: false,
                    usesPackageDates: true,
                    ...getReportPackageRowOptionDefaults(reportType),
                },
            ];
        });
    }, []);

    const updateRow = useCallback(
        (id: string, patch: ReportPackageRowPatch) => {
            setRows((prev) =>
                prev.map((row) =>
                    row.id === id
                        ? getUpdatedRow({ row, patch, rows: prev })
                        : row,
                ),
            );
        },
        [],
    );

    const updateRowTitle = useCallback((id: string, title: string) => {
        setRows((prev) =>
            prev.map((row) =>
                row.id === id ? { ...row, title, isTitleEdited: true } : row,
            ),
        );
    }, []);

    const removeRow = useCallback((id: string) => {
        setRows((prev) => prev.filter((row) => row.id !== id));
    }, []);

    const reorderRows = useCallback((activeId: string, overId: string) => {
        setRows((prev) => {
            const fromIndex = prev.findIndex((row) => row.id === activeId);
            const toIndex = prev.findIndex((row) => row.id === overId);
            if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
                return prev;
            }
            return arrayMove(prev, fromIndex, toIndex);
        });
    }, []);
    const resetRows = useCallback(() => {
        setRows([]);
    }, []);

    return {
        rows,
        addRow,
        updateRow,
        updateRowTitle,
        removeRow,
        reorderRows,
        resetRows,
    };
}
